import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Calendar, Copy, RefreshCw, Smartphone, Monitor, Mail, QrCode } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";

export default function CalendarSync() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [showQR, setShowQR] = useState(false);

  useEffect(() => {
    api
      .get("/calendar/url")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de récupérer votre calendrier"))
      .finally(() => setLoading(false));
  }, []);

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Lien copié !");
  };

  const rotate = async () => {
    if (!window.confirm("Générer une nouvelle URL ? L'ancien lien cessera immédiatement de fonctionner sur tous les appareils actuellement abonnés.")) return;
    setRotating(true);
    try {
      const { data: fresh } = await api.post("/calendar/rotate");
      setData({ ...data, ...fresh });
      toast.success("Nouvelle URL générée");
    } catch {
      toast.error("Erreur lors de la rotation");
    } finally {
      setRotating(false);
    }
  };

  return (
    <AppShell>
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <Link to="/settings"><Button variant="ghost" size="icon" className="rounded-full"><ArrowLeft className="w-5 h-5" /></Button></Link>
        <div>
          <h1 className="font-display text-2xl font-bold text-[var(--text)]">Synchronisation calendrier</h1>
          <p className="text-sm text-zinc-500">Vos RDV, relances et rappels dans iPhone, Google Agenda ou Outlook</p>
        </div>
      </div>

      {loading && <div className="text-center text-zinc-500 py-10">Chargement…</div>}

      {!loading && data && (
        <div className="px-5 space-y-5">
          {/* Calendar URL card */}
          <div className="bg-gradient-to-br from-emerald-500 to-orange-600 text-white rounded-3xl p-5 shadow-lg shadow-emerald-300/40">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-2xl bg-white/20 grid place-items-center">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">Votre URL personnelle</div>
                <div className="font-display text-lg font-bold leading-tight">Flux iCalendar</div>
              </div>
            </div>
            <div className="bg-white/15 backdrop-blur-sm rounded-xl p-2.5 flex items-center gap-2">
              <div className="flex-1 min-w-0 font-mono text-[11px] truncate select-all" data-testid="calendar-url">{data.ics_url}</div>
              <button onClick={() => copy(data.ics_url)} className="shrink-0 bg-white/20 hover:bg-[var(--surface)]/30 rounded-lg px-3 py-1.5 text-xs font-semibold flex items-center gap-1 transition-colors" data-testid="copy-url">
                <Copy className="w-3.5 h-3.5" /> Copier
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <a href={data.webcal_url} className="bg-white text-emerald-400 hover:bg-emerald-500/10 rounded-full px-4 py-1.5 text-xs font-bold inline-flex items-center gap-1.5" data-testid="subscribe-webcal">
                <Smartphone className="w-3.5 h-3.5" /> S'abonner (iPhone / Mac)
              </a>
              <button onClick={() => setShowQR(!showQR)} className="bg-white/20 hover:bg-[var(--surface)]/30 rounded-full px-4 py-1.5 text-xs font-bold inline-flex items-center gap-1.5" data-testid="toggle-qr">
                <QrCode className="w-3.5 h-3.5" /> {showQR ? "Masquer" : "Afficher"} le QR
              </button>
            </div>
            {showQR && (
              <div className="mt-4 bg-[var(--surface)] rounded-xl p-4 flex items-center justify-center" data-testid="calendar-qr">
                <QRCodeSVG value={data.webcal_url} size={180} />
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-[var(--surface)] rounded-xl p-5 border border-[var(--border-soft)] shadow-sm">
            <div className="font-display font-bold text-[var(--text)] mb-3 flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-emerald-400" /> Comment s'abonner
            </div>
            <div className="space-y-4 text-sm">
              <InstructionBlock
                icon={<Smartphone className="w-4 h-4" />}
                title="iPhone / iPad (Apple Calendar)"
                steps={[
                  'Scannez le QR code ci-dessus OU envoyez-vous le lien par email',
                  "Appuyez sur \"S'abonner (iPhone / Mac)\" — iOS ouvre Réglages → Calendrier",
                  "Validez — les événements apparaissent dans l'app Calendrier",
                  "Synchronisation automatique toutes les 15 min",
                ]}
              />
              <InstructionBlock
                icon={<Monitor className="w-4 h-4" />}
                title="Google Agenda"
                steps={[
                  "Allez sur calendar.google.com",
                  'Menu ⚙ → Paramètres → "Ajouter un agenda" → "À partir de l\'URL"',
                  `Collez : ${data.ics_url}`,
                  "Cliquez sur \"Ajouter un agenda\"",
                ]}
              />
              <InstructionBlock
                icon={<Monitor className="w-4 h-4" />}
                title="Outlook / Microsoft 365"
                steps={[
                  "Allez sur outlook.live.com → Calendrier",
                  'Barre latérale → "Ajouter un calendrier" → "S\'abonner à partir du Web"',
                  `Collez : ${data.ics_url}`,
                  "Donnez un nom (ex: Prosperr) et importez",
                ]}
              />
            </div>
          </div>

          {/* Security & rotation */}
          <div className="bg-amber-500/10 border border-amber-200 rounded-2xl p-4">
            <div className="flex items-start gap-3">
              <div className="text-amber-300 shrink-0 mt-0.5">⚠</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-amber-900 text-sm">Votre URL est secrète</div>
                <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                  Toute personne qui dispose de ce lien peut voir vos RDV. Si vous soupçonnez un partage non désiré, régénérez-la. Les anciens abonnements cesseront immédiatement.
                </p>
                <Button
                  onClick={rotate}
                  disabled={rotating}
                  variant="outline"
                  size="sm"
                  className="mt-3 rounded-full border-amber-300 text-amber-800 hover:bg-amber-500/15"
                  data-testid="rotate-btn"
                >
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${rotating ? "animate-spin" : ""}`} />
                  Régénérer l'URL
                </Button>
              </div>
            </div>
          </div>

          {/* What's synced */}
          <div className="bg-[var(--surface)] rounded-xl p-4 border border-[var(--border-soft)]">
            <div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2">Événements synchronisés</div>
            <ul className="text-sm text-zinc-300 space-y-1.5">
              <li className="flex items-center gap-2"><span>📞</span> Appels planifiés</li>
              <li className="flex items-center gap-2"><span>✉️</span> Emails à envoyer</li>
              <li className="flex items-center gap-2"><span>🚶</span> Visites terrain</li>
              <li className="flex items-center gap-2"><span>💬</span> SMS programmés</li>
              <li className="flex items-center gap-2"><span>📅</span> RDV confirmés avec les leads</li>
            </ul>
            <p className="text-xs text-zinc-500 mt-3">Lecture seule — pour modifier un événement, rendez-vous sur Prosperr. Les modifications se propagent automatiquement sous 15 min.</p>
          </div>
        </div>
      )}
    </AppShell>
  );
}

function InstructionBlock({ icon, title, steps }) {
  return (
    <div>
      <div className="font-semibold text-[var(--text)] flex items-center gap-2 mb-1.5">
        <span className="w-6 h-6 rounded-lg bg-emerald-500/15 text-emerald-400 grid place-items-center">{icon}</span>
        {title}
      </div>
      <ol className="pl-8 space-y-1 list-decimal text-zinc-400 text-xs leading-relaxed">
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
    </div>
  );
}
